import { FC, useCallback, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { saveSettingsPage } from "src/redux/company/actions";
import { tReduxState } from "src/redux/types";
import { tUserPath } from "src/redux/user/types";
import { kPhonePattern } from "src/util/constants";
import { formatPhoneNumber } from "src/util/gFormatting";
import { Spacer } from ".";
import { ValidatedTextFormInput } from "./FormInputs";
import { PdButton, PdButtonLabel } from "./forms/button";
import { PdStack } from "./layout";
import { useIsMobile } from "src/hooks/ui";
import { useConfirmModal } from "src/hooks/modals";
import { logout, sendResetPasswordEmail } from "src/redux/user/actions";

const userNameSelector = ({ UserReducer }: tReduxState) => UserReducer.profile.userName;
const userTitleSelector = ({ UserReducer }: tReduxState) => UserReducer.profile.userTitle;
const emailSelector = ({ UserReducer }: tReduxState) => UserReducer.profile.email;
const phoneSelector = ({ UserReducer }: tReduxState) => formatPhoneNumber(UserReducer.profile.phone);

const UserSettings: FC<{
  isValid?: { [key: string]: boolean };
}> = ({ isValid: _myIsValid }) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const rLoading = useSelector((state) => state.LoadingReducer.userFetching);
  const rAdminCompany = useSelector((state) => state.AdminSettingReducer);
  const rUser = useSelector((state) => state.UserReducer);
  const [sIsValid, setIsValid] = useState<{ [key: string]: boolean }>({});
  const rIsProfileUpdating = useSelector((state) => state.LoadingReducer.profileUpdating);
  const confirmModal = useConfirmModal();

  const handleOnPressChangePassword = async () => {
    const response = await confirmModal.openAsync({
      title: "Reset Password",
      description:
        "For your security, in order to reset your password, you will be logged out. Would you like to continue?",
    });
    if (response.status === "closed") return;

    dispatch(sendResetPasswordEmail(rUser.profile.email));
    dispatch(logout());
  };

  const saveSettings = useCallback(() => dispatch(saveSettingsPage(rAdminCompany)), [dispatch, rAdminCompany]);

  return (
    <View style={styles.P09_PageContainer}>
      <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
        <View style={styles.rowColumn}>
          <Text style={styles.gFormHeader}>Personal Account Information</Text>
          <Text>This information is private and will not be shared</Text>
        </View>
      </View>
      <View style={styles.formControl}>
        <View style={styles.gFormRowNoMargin}>
          <ValidatedTextFormInput<tUserPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            dispatchAction={{ type: "UPDATE_USER_DATA", userPart: { userName: "" } }}
            dispatchPath="userPart.userName"
            selectorFunction={userNameSelector}
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "Your full name",
            }}
            uniqueName="userName"
            controller={sIsValid}
            setController={setIsValid}
            myTitle="Primary User Name*"
            rules={{ required: { message: "Required" } }}
            loading={rLoading}
          />
          <Spacer />
          <ValidatedTextFormInput<tUserPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            dispatchAction={{ type: "UPDATE_USER_DATA", userPart: { userTitle: "" } }}
            dispatchPath="userPart.userTitle"
            selectorFunction={userTitleSelector}
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "Buyer & Owner",
            }}
            uniqueName="userTitle"
            controller={sIsValid}
            setController={setIsValid}
            myTitle="User Role*"
            rules={{ required: { message: "Required" } }}
            loading={rLoading}
          />
        </View>
        <View style={styles.gFormRowNoMargin}>
          <ValidatedTextFormInput<tUserPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            dispatchAction={{ type: "UPDATE_USER_DATA", userPart: { email: "" } }}
            dispatchPath="userPart.email"
            textInputProps={{
              style: styles.gLinkBankInputGray,
              placeholder: "User Email",
              editable: false,
            }}
            selectorFunction={emailSelector}
            uniqueName="email"
            controller={sIsValid}
            setController={setIsValid}
            myTitle="Account Email*"
            rules={{
              required: { message: "Required" },
              pattern: {
                pattern:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: "Please enter valid email",
              },
            }}
            loading={rLoading}
          />
          <Spacer />
          <ValidatedTextFormInput<tUserPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            dispatchAction={{ type: "UPDATE_USER_DATA", userPart: { phone: "" } }}
            dispatchPath="userPart.phone"
            selectorFunction={phoneSelector}
            uniqueName="phone"
            controller={sIsValid}
            setController={setIsValid}
            myTitle="Phone Number*"
            loading={rLoading}
            rules={{
              required: { message: "Required" },
              pattern: {
                pattern: kPhonePattern,
                message: "Please enter valid Phone Number",
              },
            }}
            textInputProps={{
              autoCompleteType: "tel",
              placeholder: "(XXX) XXX-XXXX",
              style: styles.gLinkBankInput,
            }}
          />
        </View>
        <PdStack
          isReversed={isMobile}
          direction={isMobile ? "vertical" : "horizontal"}
          style={[styles.mt6, styles.justifyBetween]}
        >
          <PdButton variant="outline" onPress={handleOnPressChangePassword} style={styles.minWMd}>
            <PdButtonLabel>Change Password</PdButtonLabel>
          </PdButton>
          <PdButton onPress={saveSettings} isLoading={rIsProfileUpdating} style={styles.minWMd}>
            <PdButtonLabel>Save</PdButtonLabel>
          </PdButton>
        </PdStack>
      </View>
    </View>
  );
};

export default UserSettings;

const styles = StyleSheet.create({
  P08_01_InputHeader: {
    ...kFonts.formLegend,
  },
  P09_PageContainer: {
    backgroundColor: kColors.white,
    flexGrow: 1,
    marginRight: 6 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
  },
  formControl: {
    marginBottom: 16,
  },
  gFormHeader: {
    ...kFonts.profileHeader,
    marginBottom: kStyleConst.kSpacing * 2,
  },
  gFormRowNoMargin: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginValidatedSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gHeaderSpacing: {
    marginVertical: 15,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputGray: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    color: kColors.mediumGrey,
    flex: 1,
    height: 36,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gSpaceBetweenRow: {
    alignItems: "baseline",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  minWMd: {
    minWidth: 150,
  },
  mt6: {
    marginTop: 6 * kStyleConst.kSpacing,
  },
  rowColumn: {
    flex: 1,
    marginBottom: 16,
    paddingEnd: 16,
  },
  rowNoMargin: {
    flexDirection: "row",
    marginTop: 0,
  },
});
